import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDeleteLog, useDomains, useLog, useZones } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import LogForm from '@/components/LogForm';
import { Pencil, Trash2 } from 'lucide-react';

export default function LogDetail() {
  const { id } = useParams<{ id: string }>();
  const q = useLog(id);
  const domainsQ = useDomains();
  const zonesQ = useZones();
  const del = useDeleteLog();
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  const log = q.data;

  const domain = (domainsQ.data ?? []).find((d) => d.id === log.domainId);
  const zone = (zonesQ.data ?? []).find((z) => z.id === log.zoneId);

  async function onDelete() {
    if (!confirm('Delete this log? This cannot be undone.')) return;
    await del.mutateAsync(log.id);
    navigate('/logs');
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <Link to="/logs" className="hover:text-foreground">
            Logs
          </Link>{' '}
          <span aria-hidden>·</span> {new Date(log.performedAt).toLocaleString()}
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {log.title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {domain ? (
            <Link
              to={`/domains/${domain.slug}`}
              className="font-mono uppercase tracking-wider hover:text-foreground"
            >
              {domain.name}
            </Link>
          ) : null}
          {zone ? (
            <span className="font-mono uppercase tracking-wider">{zone.name}</span>
          ) : null}
          {log.cost != null ? (
            <span className="font-mono tabular-nums">${(log.cost / 100).toFixed(2)}</span>
          ) : null}
        </div>
      </header>

      {log.notes ? (
        <Card>
          <CardHeader className="pb-2 space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Notes
            </p>
            <CardTitle className="font-display text-base">What happened</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 whitespace-pre-wrap leading-relaxed">{log.notes}</CardContent>
        </Card>
      ) : null}

      <div className="flex items-center gap-2 pt-2">
        <Button onClick={() => setEditing(true)} variant="secondary" data-testid="log-edit">
          <Pencil className="h-4 w-4 mr-2" aria-hidden /> Edit
        </Button>
        <Button
          onClick={onDelete}
          variant="ghost"
          className="text-destructive hover:text-destructive"
          data-testid="log-delete"
        >
          <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Delete
        </Button>
      </div>

      <LogForm open={editing} onOpenChange={setEditing} mode="edit" log={log} />
    </div>
  );
}
